import * as React from "react";
import { useListTodayActivity, type ActivityLog } from "@workspace/api-client-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { format } from "date-fns";
import {
  Plus,
  Pencil,
  CheckCircle2,
  Trash2,
  FolderInput,
  Pin,
  PinOff,
  Sparkles,
} from "lucide-react";
import { cn } from "@/lib/utils";

const ACTION_ICON: Record<string, typeof Plus> = {
  created: Plus,
  updated: Pencil,
  completed: CheckCircle2,
  deleted: Trash2,
  moved: FolderInput,
  pinned: Pin,
  unpinned: PinOff,
  renamed: Pencil,
};

const ACTION_COLOR: Record<string, string> = {
  created: "text-emerald-500 bg-emerald-500/10",
  updated: "text-blue-500 bg-blue-500/10",
  completed: "text-primary bg-primary/10",
  deleted: "text-red-500 bg-red-500/10",
  moved: "text-violet-500 bg-violet-500/10",
  pinned: "text-amber-500 bg-amber-500/10",
  unpinned: "text-muted-foreground bg-muted",
  renamed: "text-blue-500 bg-blue-500/10",
};

export function TodayMemoryPage() {
  const { data: entries = [], isLoading } = useListTodayActivity();

  return (
    <div className="max-w-4xl mx-auto h-[calc(100vh-4rem-3.25rem)] flex flex-col px-4 relative">
      <div className="pt-4 pb-3 shrink-0">
        <h1 className="text-lg font-semibold">Today's Memory</h1>
        <p className="text-sm text-muted-foreground">
          {format(new Date(), "EEEE, MMMM d")}
        </p>
      </div>

      <ScrollArea className="flex-1 -mx-4 px-4 pb-6">
        <div className="flex flex-col gap-3 py-2">
          {isLoading ? (
            <div className="text-center text-muted-foreground py-10">
              Loading today's activity...
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center text-muted-foreground py-20">
              Nothing yet today. Everything you create, edit, or complete
              will show up here.
            </div>
          ) : (
            entries.map((entry: ActivityLog) => {
              const Icon = ACTION_ICON[entry.action] ?? Sparkles;
              const colorClass =
                ACTION_COLOR[entry.action] ?? "text-muted-foreground bg-muted";
              return (
                <div
                  key={entry.id}
                  className="bg-card border rounded-2xl px-4 py-3.5 shadow-sm flex items-start gap-3"
                >
                  <div
                    className={cn(
                      "h-8 w-8 rounded-full shrink-0 flex items-center justify-center",
                      colorClass,
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[15px] font-medium">{entry.summary}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {format(new Date(entry.createdAt), "h:mm a")}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
